import React, { useState, useMemo, useEffect } from 'react';
import ConditionBuilder, { isEmptyCondition } from './segments/ConditionBuilder';
import MultiSelect from './segments/MultiSelect';
import ExportDialog from './segments/ExportDialog';
import SegmentReferences from './segments/SegmentReferences';
import { describeConditions } from './segments/conditionSummary';
import SearchableSelect from './SearchableSelect';

const csrfToken = () => document.querySelector('[name="csrf-token"]')?.content;
const jsonHeaders = () => ({
  'Content-Type': 'application/json',
  'X-CSRF-Token': csrfToken(),
});

const computationPending = (segment) => ['queued', 'running'].includes(segment.computation_status);
const formatRefreshTime = (value) => value
  ? `${new Date(value).toLocaleString('en-GB', { timeZone: 'Europe/Minsk', hour12: false })} Minsk (UTC+3)`
  : '—';
const SizeStatus = ({ segment }) => {
  const status = computationPending(segment) ? 'Updating'
    : segment.computation_status === 'failed' ? 'Error' : 'Idle';
  const count = (value) => value == null ? '—' : Number(value).toLocaleString('en-US');
  return <span className='segment-refresh-status' title={segment.segment_type === 'manual'
    ? 'Total: all saved Player IDs, including players without contacts. SMS and Email counts can overlap. Update the segment if Total is unavailable.'
    : 'Total: unique players with a phone or valid email. SMS and Email counts can overlap. Update the segment if Total is unavailable.'}>
    Total {count(segment.total_players_count)} · SMS {count(segment.players_count)} · Email {count(segment.email_players_count)} · {status}
    {segment.computation_status === 'running' && ` · ${Number(segment.processed_players || 0).toLocaleString('en-GB')} checked`}
  </span>;
};

const GearIcon = () => (
  <svg className='w-4 h-4' fill='none' viewBox='0 0 24 24' stroke='currentColor'>
    <path strokeLinecap='round' strokeLinejoin='round' strokeWidth='2' d='M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z' />
    <path strokeLinecap='round' strokeLinejoin='round' strokeWidth='2' d='M15 12a3 3 0 11-6 0 3 3 0 016 0z' />
  </svg>
);
const FileIcon = () => (
  <svg className='w-4 h-4' fill='none' viewBox='0 0 24 24' stroke='currentColor'>
    <path strokeLinecap='round' strokeLinejoin='round' strokeWidth='2' d='M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z' />
  </svg>
);

const TYPE_META = {
  dynamic: { label: 'Dynamic', icon: <GearIcon /> },
  manual: { label: 'Manual', icon: <FileIcon /> },
};

// Multi-GEO — не отдельный тип сегмента, а его состав: у Dynamic это несколько
// выбранных стран, у Manual — игроки из разных стран в одном списке. Поэтому
// подпись строится из типа и признака, а не хранится в базе.
const isMultiGeo = (segment) => segmentGeos(segment).length > 1;

const typeLabel = (segment) =>
  `${TYPE_META[segment.segment_type].label}${isMultiGeo(segment) ? ' Multi-GEO' : ''}`;

const TYPE_FILTERS = [
  { value: 'dynamic', label: 'Dynamic' },
  { value: 'dynamic_multi', label: 'Dynamic Multi-GEO' },
  { value: 'manual', label: 'Manual' },
  { value: 'manual_multi', label: 'Manual Multi-GEO' },
];

// «Dynamic» показывает все динамические сегменты, «Dynamic Multi-GEO» — только те,
// где стран больше одной.
const matchesType = (segment, filter) => {
  if (filter === 'all') return true;

  const [type, multi] = filter.split('_');
  if (segment.segment_type !== type) return false;

  return multi ? isMultiGeo(segment) : true;
};

const fieldCls = (bad) =>
  `shadow-sm border rounded w-full py-2 px-3 text-sm text-slate-700 focus:outline-none focus:ring-1 ${
    bad ? 'border-red-400 focus:ring-red-500' : 'border-slate-300 focus:ring-blue-500'
  }`;

const FieldError = ({ msg }) => (msg ? <p className='text-xs text-red-600 mt-1'>{msg}</p> : null);

const segmentGeos = (segment) =>
  segment.geos && segment.geos.length ? segment.geos : segment.geo ? [segment.geo] : [];

const GeoChips = ({ segment, max = 3, className = '', expandable = false }) => {
  const [expanded, setExpanded] = useState(false);
  const geos = segmentGeos(segment);

  if (!geos.length) {
    return (
      <span className={`text-xs px-2 py-0.5 rounded bg-slate-100 text-slate-500 ${className}`}>
        no GEO
      </span>
    );
  }

  const collapsed = max !== null && !expanded && geos.length > max;
  const shown = collapsed ? geos.slice(0, max) : geos;

  return (
    <span className={`inline-flex items-center gap-1 flex-wrap ${className}`}>
      {shown.map((geo) => (
        <span key={geo} className='text-xs px-2 py-0.5 rounded bg-blue-50 text-blue-700'>
          {geo}
        </span>
      ))}

      {collapsed &&
        (expandable ? (
          <button
            type='button'
            className='text-xs px-2 py-0.5 rounded bg-slate-100 text-slate-600 hover:bg-slate-200'
            title={`Show all ${geos.length} countries`}
            onClick={(e) => {
              e.stopPropagation();
              setExpanded(true);
            }}
          >
            +{geos.length - max}
          </button>
        ) : (
          <span className='text-xs px-2 py-0.5 rounded bg-slate-100 text-slate-600' title={geos.join(', ')}>
            +{geos.length - max}
          </span>
        ))}

      {expandable && expanded && geos.length > max && (
        <button
          type='button'
          className='text-xs px-2 py-0.5 rounded text-slate-500 hover:text-slate-700'
          onClick={(e) => {
            e.stopPropagation();
            setExpanded(false);
          }}
        >
          collapse
        </button>
      )}
    </span>
  );
};

const SegmentForm = ({ mode, type, initial, geos, segments, library, onSaved, onCancel, onDirty }) => {
  const markDirty = () => onDirty && onDirty(true);
  const [name, setName] = useState(initial?.name || '');
  const [selectedGeos, setSelectedGeos] = useState(() =>
    initial?.selected_geos || (initial?.geos?.length ? initial.geos : initial?.geo ? [initial.geo] : [])
  );
  const [conditions, setConditions] = useState(() => initial?.conditions || []);
  const [references, setReferences] = useState(() => initial?.segment_references || initial?.filters?.segments || []);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});
  const [conditionErrors, setConditionErrors] = useState([]);

  const validate = () => {
    const e = {};
    if (!name.trim()) e.name = 'Name is required.';
    if (type === 'dynamic' && selectedGeos.length === 0 && !references.some((row) => row.operator === 'include')) {
      e.geo = 'Pick at least one country or include a segment.';
    }
    return e;
  };

  const submit = async () => {
    const errs = validate();
    setFieldErrors(errs);
    if (Object.keys(errs).length) {
      setError(Object.values(errs).join(' '));
      return;
    }
    setSaving(true);
    setError('');
    setConditionErrors([]);
    try {
      const url = mode === 'edit' ? `/segments/${initial.id}` : '/segments';
      const method = mode === 'edit' ? 'PATCH' : 'POST';
      const byId = {};
      (library?.groups || []).forEach((g) => g.attributes.forEach((a) => { byId[a.id] = a; }));
      const cleaned = conditions.filter((c) => !isEmptyCondition(c, byId[c.attribute]));

      const body = {
        segment: {
          name,
          segment_type: type,
          refresh_interval: type === 'dynamic' ? 'daily' : 'disabled',
          geos: type === 'dynamic' ? selectedGeos : [],
          filters: type === 'dynamic' ? { conditions: cleaned, segments: references } : {}
        }
      };
      const res = await fetch(url, { method, headers: jsonHeaders(), body: JSON.stringify(body) });
      let data = null;
      try {
        data = await res.json();
      } catch (_) {
      }
      if (!res.ok) {
        setConditionErrors((data && data.errors) || []);
        const msg = (data && data.errors && data.errors.join('. ')) || `Save failed (HTTP ${res.status})`;
        throw new Error(msg);
      }
      onSaved(data, mode);
    } catch (e) {
      setError(e.message);
      setSaving(false);
    }
  };

  return (
    <div className='space-y-4'>
      <div>
        <label className='block text-xs font-medium text-slate-500 mb-1'>Name *</label>
        <input
          className={fieldCls(!!fieldErrors.name)}
          value={name}
          onChange={(e) => {
            setName(e.target.value);
            setFieldErrors((prev) => ({ ...prev, name: undefined }));
            markDirty();
          }}
          placeholder='Segment name'
        />
        <FieldError msg={fieldErrors.name} />
      </div>

      {type === 'dynamic' ? (
        <div>
          <label className='block text-xs font-medium text-slate-500 mb-1'>Countries (GEO)</label>
          <MultiSelect
            options={geos}
            value={selectedGeos}
            onChange={(v) => {
              setSelectedGeos(v);
              setFieldErrors((prev) => ({ ...prev, geo: undefined }));
              markDirty();
            }}
            placeholder='Select countries…'
          />
          {fieldErrors.geo ? (
            <FieldError msg={fieldErrors.geo} />
          ) : (
            <p className='text-xs text-slate-400 mt-1'>
              Pick countries, or leave empty when using Include to keep the included segments&apos; countries.
            </p>
          )}
        </div>
      ) : (
        <p className='text-xs text-slate-400'>
          GEO is detected per player from the uploaded list, so a Static segment may hold several countries.
        </p>
      )}

      {type === 'dynamic' && (
        <div className='segment-refresh-settings'>
          <p>Automatic update: every day at 02:00 Europe/Minsk (UTC+3).</p>
          <p>Applies to all dynamic segments. Manual updates and campaign launches do not change the nightly schedule.</p>
        </div>
      )}

      {type === 'dynamic' && (
        <div className='border-t border-slate-100 pt-3'>
          <SegmentReferences segments={segments} currentId={initial?.id} references={references}
            onChange={(next) => { setReferences(next); markDirty(); }} />
        </div>
      )}

      {type === 'dynamic' && (
        <div className='border-t border-slate-100 pt-3'>
          <p className='text-xs font-semibold text-slate-600 mb-2'>
            Conditions (all of them apply — AND)
          </p>
          <ConditionBuilder
            hasIncludedSegments={references.some((row) => row.operator === 'include')}
            library={library}
            conditions={conditions}
            errors={conditionErrors}
            onChange={(next) => {
              setConditions(next);
              markDirty();
            }}
          />
        </div>
      )}

      {error && <p className='text-sm text-red-600'>{error}</p>}

      <div className='flex justify-end gap-2 pt-2'>
        <button onClick={onCancel} className='px-3 py-2 text-sm text-slate-600 bg-slate-100 hover:bg-slate-200 rounded'>Cancel</button>
        <button onClick={submit} disabled={saving} className={`px-3 py-2 text-sm text-white bg-blue-600 hover:bg-blue-700 rounded ${saving ? 'opacity-50' : ''}`}>
          {saving ? 'Saving…' : mode === 'edit' ? 'Save changes' : 'Create segment'}
        </button>
      </div>
    </div>
  );
};

const Modal = ({ title, onClose, children, wide = false }) => {
  useEffect(() => {
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previous;
    };
  }, []);

  return (
  <div className='fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4'>
    <div
      className={`bg-white rounded-lg shadow-xl w-full max-h-[90vh] overflow-y-auto ${
        wide ? 'max-w-3xl' : 'max-w-lg'
      }`}
      onClick={(e) => e.stopPropagation()}
    >
      <div className='flex items-center justify-between px-5 py-3 border-b border-slate-200'>
        <h3 className='text-base font-semibold text-slate-900'>{title}</h3>
        <button onClick={onClose} className='text-slate-400 hover:text-slate-600'>✕</button>
      </div>
      <div className='p-5'>{children}</div>
    </div>
  </div>
  );
};

const ConfirmDialog = ({ title, message, confirmLabel, cancelLabel, onConfirm, onCancel }) => (
  <div className='fixed inset-0 z-[60] flex items-center justify-center bg-black/40 p-4'>
    <div className='bg-white rounded-lg shadow-xl w-full max-w-sm' onClick={(e) => e.stopPropagation()}>
      <div className='px-5 py-4'>
        <h3 className='text-base font-semibold text-slate-900'>{title}</h3>
        <p className='mt-2 text-sm text-slate-600'>{message}</p>
      </div>
      <div className='flex justify-end gap-2 px-5 py-3 border-t border-slate-200'>
        <button onClick={onCancel} className='px-3 py-2 text-sm text-slate-600 bg-slate-100 hover:bg-slate-200 rounded'>
          {cancelLabel}
        </button>
        <button onClick={onConfirm} className='px-3 py-2 text-sm text-white bg-red-600 hover:bg-red-700 rounded'>
          {confirmLabel}
        </button>
      </div>
    </div>
  </div>
);

const DetailsPanel = ({ segment, segments, canExport, library, onClose, onEdit, onDelete, onDuplicate, duplicating, onCopyLink, onRecompute, onUpload, onClear, onExport }) => {
  const [busy, setBusy] = useState(false);
  const isManual = segment.segment_type === 'manual';
  const inUse = segment.active_campaigns > 0;
  const conditionParts = describeConditions(segment.conditions || [], library?.byId || {});

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    await onUpload(segment, file);
    setBusy(false);
    e.target.value = '';
  };

  return (
    <div className='fixed bottom-0 inset-x-0 z-40 bg-white border-t border-slate-200 shadow-[0_-4px_12px_rgba(0,0,0,0.06)]'>
      <div className='max-w-[1400px] mx-auto px-6 py-4'>
        <div className='flex items-start justify-between'>
          <div className='flex-1'>
            <div className='flex items-center gap-2'>
              <span className='text-slate-500'>{TYPE_META[segment.segment_type].icon}</span>
              <h3 className='text-lg font-semibold text-slate-900'>{segment.name}</h3>
              <span className='text-xs px-2 py-0.5 rounded bg-slate-100 text-slate-600'>{typeLabel(segment)}</span>
              <GeoChips segment={segment} max={null} />
            </div>
            <div className='mt-2 text-sm text-slate-600'>
              Players: <span className='font-semibold text-slate-900'><SizeStatus segment={segment} /></span>
              {isManual
                ? segment.uploaded_at && (
                    <span className='text-slate-400'>
                      {' '}
                      · uploaded {new Date(segment.uploaded_at).toLocaleString()}
                    </span>
                  )
                : segment.last_computed_at && (
                    <span className='text-slate-400'>
                      {' '}
                      · updated {formatRefreshTime(segment.last_computed_at)}
                    </span>
                  )}
            </div>
            {!isManual && <div className='segment-refresh-settings'>
              <p>Last successful update: {formatRefreshTime(segment.last_computed_at)}</p>
              <p>Automatic update: every day at 02:00 Europe/Minsk (UTC+3)</p>
              <p>Next update: {formatRefreshTime(segment.next_refresh_at)}</p>
              {segment.computation_status === 'failed' && <p>Update failed. Click Update segment to retry. Campaigns refresh again before sending.</p>}
            </div>}
            {segment.segment_type === 'dynamic' && (
              <div className='mt-2 text-xs text-slate-500'>
                {conditionParts.length ? (
                  <>All of: {conditionParts.join('  ·  ')}</>
                ) : (
                  <>{segment.segment_references?.some((row) => row.operator === 'include')
                    ? 'No attributes — all players from included segments.'
                    : 'No conditions — every player of the selected countries.'}</>
                )}
                {(segment.segment_references || []).map((row) => (
                  <p key={row.segment_id} className='mt-1'>
                    {row.operator === 'include' ? 'Include' : 'Exclude'}: {segments.find((item) => String(item.id) === String(row.segment_id))?.name || `#${row.segment_id}`}
                  </p>
                ))}
              </div>
            )}

            {inUse && (
              <div className='mt-2 text-xs text-amber-700'>
                In use by {segment.active_campaigns} active campaign
                {segment.active_campaigns === 1 ? '' : 's'} — only the name can be changed, and the
                segment cannot be deleted or its size updated while it is in use.
              </div>
            )}
          </div>
          <button onClick={onClose} className='text-slate-400 hover:text-slate-600 ml-4'>✕</button>
        </div>

        <div className='mt-4 flex flex-wrap gap-2'>
          {!isManual && (
            <button disabled={computationPending(segment) || segment.active_campaigns > 0}
              title={segment.active_campaigns > 0 ? 'Size cannot be updated while the segment is used by an active campaign.' : undefined}
              onClick={() => onRecompute(segment)} className='segment-refresh-button'>Update segment</button>
          )}
          <button onClick={() => onEdit(segment)} className='px-3 py-1.5 text-sm bg-blue-50 text-blue-700 hover:bg-blue-100 rounded'>Edit</button>
          <button disabled={duplicating} onClick={() => onDuplicate(segment)}
            className='px-3 py-1.5 text-sm bg-slate-100 hover:bg-slate-200 rounded disabled:opacity-50'>
            {duplicating ? 'Duplicating…' : 'Duplicate'}
          </button>
          <button onClick={() => onCopyLink(segment)} className='px-3 py-1.5 text-sm bg-slate-100 hover:bg-slate-200 rounded'>Copy link</button>
          {canExport && (
            <button
              onClick={() => onExport(segment)}
              className='px-3 py-1.5 text-sm bg-slate-100 text-slate-700 hover:bg-slate-200 rounded'
              title='Download the current player list as CSV'
            >
              Download CSV
            </button>
          )}
          {isManual && (
            <>
              <label className={`px-3 py-1.5 text-sm bg-emerald-50 text-emerald-700 hover:bg-emerald-100 rounded cursor-pointer ${busy ? 'opacity-50' : ''}`}>
                {busy ? 'Uploading…' : 'Add CSV'}
                <input type='file' accept='.csv' className='hidden' onChange={handleFile} disabled={busy} />
              </label>
              <button onClick={() => onClear(segment)} className='px-3 py-1.5 text-sm bg-amber-50 text-amber-700 hover:bg-amber-100 rounded'>Clear</button>
            </>
          )}
          <button
            onClick={() => onDelete(segment)}
            disabled={inUse}
            title={inUse ? 'Used by an active campaign' : undefined}
            className={`px-3 py-1.5 text-sm rounded ml-auto ${
              inUse
                ? 'bg-slate-100 text-slate-400 cursor-not-allowed'
                : 'bg-red-50 text-red-600 hover:bg-red-100'
            }`}
          >
            Delete
          </button>
        </div>
      </div>
    </div>
  );
};

const SegmentsRegistry = ({ initialSegments, initialDetailsId, geos, canExport }) => {
  const [segments, setSegments] = useState(initialSegments || []);
  const [search, setSearch] = useState('');
  const [geoFilter, setGeoFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [selected, setSelected] = useState([]);
  const [detailsId, setDetailsId] = useState(initialDetailsId || null);
  const [duplicating, setDuplicating] = useState(false);
  const [modal, setModal] = useState(null);
  const [notice, setNotice] = useState(null);
  const [formDirty, setFormDirty] = useState(false);
  const [confirmDiscard, setConfirmDiscard] = useState(false);
  const [library, setLibrary] = useState(null);
  const [exportSegment, setExportSegment] = useState(null);

  const openDetails = (id) => {
    setDetailsId(id);
    const path = id ? `/segments/${id}` : '/segments';
    if (window.location.pathname !== path) window.history.pushState({}, '', path);
  };

  useEffect(() => {
    const onPopState = () => {
      const match = window.location.pathname.match(/^\/segments\/(\d+)\/?$/);
      setDetailsId(match ? Number(match[1]) : null);
    };
    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, []);

  useEffect(() => {
    const load = async () => {
      try {
        const [filterRes, exportRes] = await Promise.all([
          fetch('/api/v1/attributes', { headers: { 'X-Requested-With': 'XMLHttpRequest' } }),
          fetch('/api/v1/attributes?for=export', { headers: { 'X-Requested-With': 'XMLHttpRequest' } })
        ]);
        if (!filterRes.ok) return;

        const data = await filterRes.json();
        const exportData = exportRes.ok ? await exportRes.json() : { groups: [] };
        const byId = {};
        data.groups.forEach((g) => g.attributes.forEach((a) => { byId[a.id] = a; }));
        exportData.groups.forEach((g) => g.attributes.forEach((a) => { byId[a.id] = byId[a.id] || a; }));

        setLibrary({ ...data, byId, exportGroups: exportData.groups });
      } catch (e) {
        console.error('Failed to load attribute library:', e);
      }
    };
    load();
  }, []);

  const hasPendingComputations = segments.some(computationPending);
  useEffect(() => {
    let active = true;
    let timer;
    const refresh = async () => {
      try {
        const res = await fetch('/segments.json', { headers: { 'X-Requested-With': 'XMLHttpRequest' } });
        if (res.ok) {
          const data = await res.json();
          if (active) setSegments((previous) => previous.map((segment) => {
            const fresh = data.initialSegments.find((item) => item.id === segment.id);
            return fresh ? { ...segment, ...fresh } : segment;
          }));
        }
      } catch (e) {
        console.error('Failed to refresh segment sizes:', e);
      } finally {
        if (active) timer = setTimeout(refresh, hasPendingComputations ? 3000 : 15000);
      }
    };
    timer = setTimeout(refresh, hasPendingComputations ? 3000 : 15000);
    return () => { active = false; clearTimeout(timer); };
  }, [hasPendingComputations]);

  const closeModal = () => {
    if (formDirty) {
      setConfirmDiscard(true);
      return;
    }
    setModal(null);
  };

  const discardAndClose = () => {
    setFormDirty(false);
    setConfirmDiscard(false);
    setModal(null);
  };

  const geoOptions =
    geos && geos.length
      ? geos
      : Array.from(new Set(segments.flatMap((s) => segmentGeos(s)))).map((g) => ({ value: g, label: g }));

  const visible = useMemo(
    () =>
      segments.filter((s) => {
        if (search && !s.name.toLowerCase().includes(search.toLowerCase())) return false;
        if (geoFilter !== 'all' && !segmentGeos(s).includes(geoFilter)) return false;
        if (!matchesType(s, typeFilter)) return false;
        return true;
      }),
    [segments, search, geoFilter, typeFilter]
  );

  const detailsSegment = segments.find((s) => s.id === detailsId) || null;

  const upsert = (seg) =>
    setSegments((prev) => {
      const idx = prev.findIndex((s) => s.id === seg.id);
      if (idx === -1) return [seg, ...prev];
      const copy = [...prev];
      copy[idx] = seg;
      return copy;
    });

  const patchSegment = (id, attrs) =>
    setSegments((prev) => prev.map((s) => (s.id === id ? { ...s, ...attrs } : s)));

  const patchCount = (id, players_count, last_computed_at) =>
    setSegments((prev) => prev.map((s) => (s.id === id ? { ...s, players_count, last_computed_at: last_computed_at ?? s.last_computed_at } : s)));

  const toggleSelect = (id) =>
    setSelected((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));

  const visibleIds = visible.map((s) => s.id);
  const selectedVisible = visibleIds.filter((id) => selected.includes(id));
  const allVisibleSelected = visibleIds.length > 0 && selectedVisible.length === visibleIds.length;

  const toggleSelectAll = () =>
    setSelected((prev) =>
      allVisibleSelected
        ? prev.filter((id) => !visibleIds.includes(id))
        : Array.from(new Set([...prev, ...visibleIds]))
    );

  const onSaved = (seg, mode) => {
    setFormDirty(false);
    upsert(seg);
    setModal(null);
    if (mode === 'create') openDetails(seg.id);
    refreshSegments();
  };

  const refreshSegments = async () => {
    try {
      const res = await fetch('/segments.json', { headers: { 'X-Requested-With': 'XMLHttpRequest' } });
      if (res.ok) setSegments((await res.json()).initialSegments);
    } catch (e) {
      console.error('Failed to refresh segments:', e);
    }
  };

  const handleDuplicate = async (seg) => {
    if (duplicating) return;
    setDuplicating(true);
    try {
      const res = await fetch(`/segments/${seg.id}/duplicate`, { method: 'POST', headers: jsonHeaders() });
      const data = await res.json();
      if (!res.ok) throw new Error((data.errors || ['Duplicate failed.']).join(', '));
      upsert(data);
      openDetails(data.id);
      setNotice({ type: 'success', text: `Created “${data.name}”.` });
      setModal({ mode: 'edit', type: data.segment_type, segment: data });
    } catch (error) {
      setNotice({ type: 'error', text: error.message || 'Duplicate failed.' });
    } finally {
      setDuplicating(false);
    }
  };

  const handleCopyLink = async (seg) => {
    const link = `${window.location.origin}/segments/${seg.id}`;
    try {
      await navigator.clipboard.writeText(link);
      setNotice({ type: 'success', text: 'Segment link copied.' });
    } catch (error) {
      setNotice({ type: 'error', text: `Copy this link: ${link}` });
    }
  };

  const handleDelete = async (seg) => {
    if (!window.confirm(`Delete segment "${seg.name}"? This cannot be undone.`)) return;
    const res = await fetch(`/segments/${seg.id}`, { method: 'DELETE', headers: jsonHeaders() });
    if (!res.ok) {
      const data = await res.json().catch(() => ({}));
      setNotice({ type: 'error', text: (data.errors || ['Delete failed']).join(', ') });
      return;
    }
    setSegments((prev) => prev.filter((s) => s.id !== seg.id));
    if (detailsId === seg.id) openDetails(null);
    setSelected((prev) => prev.filter((x) => x !== seg.id));
    setNotice(null);
  };

  const handleBulkDelete = async () => {
    if (!selected.length) return;
    if (!window.confirm(`Delete ${selected.length} selected segment(s)? This cannot be undone.`))
      return;

    const res = await fetch('/segments/bulk_destroy', {
      method: 'POST',
      headers: jsonHeaders(),
      body: JSON.stringify({ ids: selected }),
    });
    if (!res.ok) {
      setNotice({ type: 'error', text: 'Delete failed.' });
      return;
    }

    const data = await res.json();
    const deletedIds = data.deleted_ids || [];
    setSegments((prev) => prev.filter((s) => !deletedIds.includes(s.id)));
    if (deletedIds.includes(detailsId)) openDetails(null);
    setSelected((prev) => prev.filter((id) => !deletedIds.includes(id)));

    const blocked = data.blocked || [];
    setNotice(
      blocked.length
        ? {
            type: 'warning',
            text: `Deleted ${data.deleted}. Kept ${blocked.length} still used by campaigns or other segments: ${blocked
              .map((b) => b.name)
              .join(', ')}.`,
          }
        : { type: 'success', text: `Deleted ${data.deleted} segment(s).` }
    );
  };

  const handleRecompute = async (seg) => {
    const res = await fetch(`/segments/${seg.id}/recompute`, { method: 'POST', headers: jsonHeaders() });
    const data = await res.json();
    if (!res.ok) {
      setNotice({ type: 'error', text: (data.errors || []).join(', ') || 'Could not queue size calculation. Try again.' });
      return;
    }
    patchSegment(seg.id, data);
    refreshSegments();
  };

  const handleUpload = async (seg, file) => {
    const fd = new FormData();
    fd.append('file', file);
    const res = await fetch(`/segments/${seg.id}/members`, { method: 'POST', headers: { 'X-CSRF-Token': csrfToken() }, body: fd });
    const data = await res.json();
    if (!res.ok) {
      window.alert(data.error || 'Upload failed.');
      return;
    }

    patchSegment(seg.id, {
      players_count: data.players_count,
      total_players_count: data.total_players_count,
      uploaded_at: data.uploaded_at,
      geos: data.geos,
    });
    refreshSegments();

    const skipped = [
      data.duplicates ? `${data.duplicates} already in segment` : null,
      data.not_found ? `${data.not_found} not found` : null,
      data.no_contact ? `${data.no_contact} without a phone number or valid email` : null,
      data.invalid_id ? `${data.invalid_id} invalid Player ID` : null,
    ].filter(Boolean);

    window.alert(
      `Added ${data.added} of ${data.rows} players.` +
        (skipped.length ? `\nSkipped: ${skipped.join(', ')}.` : '')
    );
  };

  const handleClear = async (seg) => {
    if (!window.confirm('Clear all players from this segment?')) return;
    await fetch(`/segments/${seg.id}/members`, { method: 'DELETE', headers: jsonHeaders() });
    patchSegment(seg.id, { players_count: 0, total_players_count: 0, email_players_count: 0, uploaded_at: null });
    refreshSegments();
  };

  const input =
    'shadow-sm border border-slate-300 rounded px-2 py-1.5 text-sm text-slate-700 focus:outline-none focus:ring-1 focus:ring-blue-500';

  return (
    <div className='segments-registry min-h-screen bg-slate-50'>
      <div className='max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 py-6 pb-40'>
        <div className='flex items-center justify-between mb-6'>
          <div>
            <h1 className='text-xl font-semibold text-slate-900'>Segments</h1>
          </div>
          <button onClick={() => setModal({ mode: 'create', type: null })} className='inline-flex items-center px-3 py-2 text-sm font-medium text-white bg-blue-600 rounded hover:bg-blue-700 shadow-sm'>
            + Create Segment
          </button>
        </div>

        <div className='bg-white rounded-lg border border-slate-200 p-3 mb-4 flex flex-wrap items-end gap-3'>
          <div className='flex-1 min-w-[200px]'>
            <label className='block text-xs font-medium text-slate-500 mb-1'>Search by name</label>
            <input className={`${input} w-full`} value={search} onChange={(e) => setSearch(e.target.value)} placeholder='Segment name…' />
          </div>
          <div className='min-w-[150px]'>
            <label className='block text-xs font-medium text-slate-500 mb-1'>GEO</label>
            <SearchableSelect
              options={geoOptions}
              value={geoFilter}
              onChange={(v) => setGeoFilter(v || 'all')}
              includeAllOption
              allLabel='All GEO'
              placeholder='All GEO'
            />
          </div>
          <div className='min-w-[190px]'>
            <label className='block text-xs font-medium text-slate-500 mb-1'>Type</label>
            <SearchableSelect
              options={TYPE_FILTERS}
              value={typeFilter}
              onChange={(v) => setTypeFilter(v || 'all')}
              includeAllOption
              allLabel='All types'
              placeholder='All types'
              isSearchable={false}
            />
          </div>
        </div>

        {notice && (
          <div
            className={`mb-4 px-4 py-3 rounded-lg border text-sm ${
              notice.type === 'error'
                ? 'bg-red-50 border-red-200 text-red-700'
                : notice.type === 'warning'
                ? 'bg-amber-50 border-amber-200 text-amber-800'
                : 'bg-emerald-50 border-emerald-200 text-emerald-700'
            }`}
          >
            {notice.text}
          </div>
        )}

        <div className='bg-white rounded-lg border border-slate-200 overflow-hidden'>
          <div className='flex items-center px-4 py-2.5 bg-slate-50 border-b border-slate-200'>
            <input
              type='checkbox'
              className='mr-3 h-4 w-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500'
              checked={allVisibleSelected}
              ref={(el) => {
                if (el) el.indeterminate = selectedVisible.length > 0 && !allVisibleSelected;
              }}
              onChange={toggleSelectAll}
              disabled={visible.length === 0}
              title='Select all'
            />
            {selected.length > 0 ? (
              <>
                <span className='text-sm font-medium text-slate-700'>
                  {selected.length} selected
                </span>
                <button
                  onClick={() => setSelected([])}
                  className='ml-3 text-sm text-slate-500 hover:text-slate-700'
                >
                  Clear
                </button>
                <button
                  onClick={handleBulkDelete}
                  className='ml-auto inline-flex items-center px-3 py-1.5 text-sm font-medium text-red-600 bg-red-50 hover:bg-red-100 rounded'
                >
                  <svg className='w-4 h-4 mr-1.5' viewBox='0 0 24 24' fill='none' stroke='currentColor'>
                    <path
                      strokeLinecap='round'
                      strokeLinejoin='round'
                      strokeWidth='2'
                      d='M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16'
                    />
                  </svg>
                  Delete selected ({selected.length})
                </button>
              </>
            ) : (
              <span className='text-xs font-medium text-slate-500 uppercase'>
                {visible.length} segment{visible.length === 1 ? '' : 's'}
              </span>
            )}
          </div>

          <div className='divide-y divide-slate-100'>
            {visible.map((s) => (
              <div
                key={s.id}
                className={`flex items-center px-4 py-3 ${
                  selected.includes(s.id) ? 'bg-blue-50/50' : 'hover:bg-slate-50'
                }`}
              >
                <input
                  type='checkbox'
                  className='mr-3 h-4 w-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500'
                  checked={selected.includes(s.id)}
                  onChange={() => toggleSelect(s.id)}
                />
                <span className='text-slate-400 mr-2' title={typeLabel(s)}>
                  {TYPE_META[s.segment_type].icon}
                </span>
                <a href={`/segments/${s.id}`} className='font-medium text-slate-900 hover:text-blue-600 mr-3'
                  onClick={(event) => {
                    if (event.ctrlKey || event.metaKey || event.shiftKey || event.altKey) return;
                    event.preventDefault();
                    openDetails(s.id);
                  }}>{s.name}</a>
                <span className='text-xs px-2 py-0.5 rounded bg-slate-100 text-slate-600 mr-3 whitespace-nowrap'>
                  {typeLabel(s)}
                </span>
                <GeoChips segment={s} className='mr-3' expandable />
                <span className='text-sm text-slate-500 mr-3'>players: <SizeStatus segment={s} /></span>
                {s.active_campaigns > 0 && (
                  <span
                    className='text-xs px-2 py-0.5 rounded bg-amber-50 text-amber-700 border border-amber-200'
                    title='Used by active campaigns — cannot be deleted'
                  >
                    in use: {s.active_campaigns}
                  </span>
                )}
                <button
                  onClick={() => openDetails(s.id)}
                  className='ml-auto px-3 py-1 text-sm text-blue-600 hover:bg-blue-50 rounded'
                >
                  Details
                </button>
              </div>
            ))}
            {visible.length === 0 && (
              <div className='px-4 py-10 text-center text-sm text-slate-500'>No segments found.</div>
            )}
          </div>
        </div>
      </div>

      {modal && (
        <Modal
          title={modal.mode === 'edit' ? 'Edit segment' : modal.type ? `New ${TYPE_META[modal.type].label} segment` : 'Create segment'}
          onClose={closeModal}
          wide={modal.type === 'dynamic'}
        >
          {modal.mode === 'create' && !modal.type ? (
            <div className='grid grid-cols-2 gap-3'>
              <button onClick={() => setModal({ mode: 'create', type: 'dynamic' })} className='flex flex-col items-center gap-2 p-6 border border-slate-200 rounded-lg hover:border-blue-400 hover:bg-blue-50'>
                <span className='text-blue-600'><GearIcon /></span>
                <span className='font-medium text-slate-800'>Dynamic</span>
                <span className='text-xs text-slate-500 text-center'>Rules over live API data</span>
              </button>
              <button onClick={() => setModal({ mode: 'create', type: 'manual' })} className='flex flex-col items-center gap-2 p-6 border border-slate-200 rounded-lg hover:border-blue-400 hover:bg-blue-50'>
                <span className='text-blue-600'><FileIcon /></span>
                <span className='font-medium text-slate-800'>Manual</span>
                <span className='text-xs text-slate-500 text-center'>Upload a CSV list</span>
              </button>
            </div>
          ) : (
            <SegmentForm
              mode={modal.mode}
              type={modal.type}
              initial={modal.segment}
              geos={geoOptions}
              segments={segments}
              library={library}
              onSaved={onSaved}
              onCancel={closeModal}
              onDirty={setFormDirty}
            />
          )}
        </Modal>
      )}

      {confirmDiscard && (
        <ConfirmDialog
          title='Discard changes?'
          message='You have unsaved changes. If you close now, your input will be lost.'
          confirmLabel='Discard'
          cancelLabel='Keep editing'
          onConfirm={discardAndClose}
          onCancel={() => setConfirmDiscard(false)}
        />
      )}

      {exportSegment && (
        <Modal title={`Download “${exportSegment.name}”`} onClose={() => setExportSegment(null)}>
          <ExportDialog
            segment={exportSegment}
            library={library}
            onClose={() => setExportSegment(null)}
          />
        </Modal>
      )}

      {detailsSegment && (
        <DetailsPanel
          segment={detailsSegment}
          segments={segments}
          canExport={canExport}
          library={library}
          onExport={setExportSegment}
          onClose={() => openDetails(null)}
          onDuplicate={handleDuplicate}
          duplicating={duplicating}
          onCopyLink={handleCopyLink}
          onEdit={(seg) => setModal({ mode: 'edit', type: seg.segment_type, segment: seg })}
          onDelete={handleDelete}
          onRecompute={handleRecompute}
          onUpload={handleUpload}
          onClear={handleClear}
        />
      )}
    </div>
  );
};

export default SegmentsRegistry;
